import type { Locale } from './i18n';

/**
 * What the server says to the app (1.19): error messages of the callables and the sync errors
 * it stores (calendar, bins, Bring!). The server keeps speaking German – the app translates a
 * German message for an English device with this dictionary, so old and new apps both work.
 * A test checks that every message in functions/src has an entry here.
 */
export const SERVER_TEXT = {
  de: {
    signIn: 'Bitte zuerst anmelden.',
    parentsOnly: 'Das dürfen nur Eltern.',
    parentsTeens: 'Das dürfen nur Eltern und Teens.',
    badInput: 'Die Eingabe ist unvollständig oder ungültig.',
    noHousehold: 'Dieses Konto gehört noch zu keinem Haushalt.',
    hasHousehold: 'Dieses Konto hat schon einen Haushalt.',
    reload: 'Bitte die Seite neu laden und nochmal versuchen.',
    emailOrGoogle: 'Bitte mit E-Mail oder Google anmelden.',
    badInvite: 'Der Code ist ungültig oder abgelaufen.',
    parentsStay: 'Eltern bleiben Eltern.',
    kidsUseWall: 'Kinder nutzen das Familiengerät.',
    confirmItsYou: 'Bitte bestätige noch einmal, dass du es bist.',
    passwordOnly: 'Das geht nur für Konten mit E-Mail und Passwort.',
    sameAddress: 'Das ist schon deine Adresse.',
    noEmail: 'Dieses Konto hat keine E-Mail.',
    sentAlready: 'Gerade schon verschickt – schau bitte ins Postfach.',
    sentAlreadyNew: 'Gerade schon verschickt – schau bitte ins Postfach der neuen Adresse.',
    emailTaken: 'Mit dieser E-Mail gibt es schon ein Konto.',
    noPage: 'Diese Seite gibt es nicht.',
    exportParents: 'Den Datenexport können nur Eltern herunterladen.',
    feedbackLimit: 'Danke – das war gerade viel auf einmal. Bitte versuch es später noch einmal.',
    notEnoughStars: 'Dafür reichen die Sterne noch nicht.',
    requestGone: 'Diese Anfrage gibt es nicht mehr.',
    taskGone: 'Diese Aufgabe gibt es nicht mehr.',
    noPerson: 'Diese Person gibt es nicht.',
    badPairCode: 'Der Code stimmt nicht oder ist abgelaufen.',
    deviceGone: 'Das Gerät ist nicht gekoppelt.',
    pairLimit: 'Zu viele falsche Codes. Bitte in einer Viertelstunde nochmal versuchen.',
    pinWaitOne: 'Zu viele Versuche. Bitte eine Minute warten.',
    pinWait: (minutes: string) => `Zu viele Versuche. Bitte ${minutes} Minuten warten.`,
    // AI helper
    aiEmpty: 'Bitte etwas schreiben, sagen oder ein Foto wählen.',
    aiOff: 'Der KI-Helfer ist ausgeschaltet. Eltern können ihn in den Einstellungen einschalten.',
    aiLimit: (n: string) =>
      `Heute wurde der KI-Helfer schon ${n}-mal benutzt. Morgen geht es wieder.`,
    // the cost cap (1.28.1)
    aiPauseApp:
      'Der KI-Helfer macht für heute Pause – morgen geht es wieder. Bis dahin einfach von Hand eintragen.',
    aiPauseFamily:
      'Eure KI-Anfragen für heute sind aufgebraucht – morgen geht es wieder. Bis dahin einfach von Hand eintragen.',
    aiNothing: 'Dazu konnte die KI nichts vorschlagen. Probier es mit anderen Worten.',
    aiBusy:
      'Der KI-Helfer ist gerade ausgelastet. In ein paar Minuten geht es wieder – oder jetzt einfach von Hand eintragen.',
    aiUnavailable: 'Der KI-Helfer ist gerade nicht verfügbar. Bitte später nochmal versuchen.',
    aiGarbled: 'Die KI hat unverständlich geantwortet. Bitte nochmal versuchen.',
    aiUnreachable: 'Die KI ist gerade nicht erreichbar. Bitte gleich nochmal versuchen.',
    aiUnreachableShort: 'Die KI ist gerade nicht erreichbar.',
    aiParents: 'Den KI-Helfer nutzen nur Eltern – am Wanddisplay mit der Eltern-PIN.',
    aiSignIn: 'Die Anmeldung bei der KI hat nicht geklappt.',
    wallOnly: 'Nur am Wanddisplay.',
    aiQuota: 'Die KI ist gerade ausgelastet.',
    aiKey: 'Der Schlüssel wird von Google nicht angenommen.',
    aiRefused: 'Die KI wollte dazu nichts sagen.',
    aiGarbledShort: 'Die KI hat unverständlich geantwortet.',
    // calendars
    notCalendarLink: 'Das ist kein Kalender-Link (https:// oder webcal://).',
    calendarUnreachable: 'Der Kalender ist nicht erreichbar. Ist der Link vollständig kopiert?',
    noCalendar: 'Unter diesem Link liegt kein Kalender. Bitte den iCal-/ICS-Link nehmen.',
    calendarStatus: (status: string) => `Kalender nicht erreichbar (${status})`,
    noCalendarLink: 'Kein Kalender-Link hinterlegt',
    badLink: 'Das ist kein gültiger Link.',
    tooBig: 'Der Kalender ist zu groß.',
    notPublic: 'Dieser Link zeigt auf keine öffentliche Adresse.',
    httpsOnly: 'Nur Links mit https:// oder http:// gehen.',
    redirects: 'Der Link leitet zu oft weiter.',
    // bins
    wasteDown: 'Der Abfall-Dienst antwortet gerade nicht. Bitte später nochmal.',
    wasteUnknown: 'Diesen Abfall-Betrieb kennen wir noch nicht.',
    wasteNoDates: 'Für diese Straße gibt es keine Termine.',
    wasteNoData: 'Abfall-Dienst: keine Daten',
    wasteNoEvents: 'Abfall-Dienst: keine Termine',
    wasteStatus: (status: string) => `Abfall-Dienst antwortet nicht (${status})`,
    wasteBadAddress: 'Ungültige Adresse',
    wasteNoAddress: 'Keine Adresse für die Müllabfuhr hinterlegt',
    // Bring!
    bringAuth:
      'Bring! möchte eine neue Anmeldung – in den Einstellungen unter „Einkaufsliste“ neu verbinden.',
    bringDown: 'Bring! ist gerade nicht erreichbar. Bitte gleich nochmal versuchen.',
    bringChanged:
      'Bring! hat etwas geändert – die Einkaufsliste funktioniert gerade nicht. Wir kümmern uns darum.',
    bringNotConnected: 'Bring! ist noch nicht verbunden.',
    bringNoList: 'Diese Liste gibt es in Bring! nicht (mehr).',
    bringEmptyAccount: 'In diesem Bring!-Konto gibt es noch keine Liste.',
    bringRejected: 'Bring! hat die Anmeldung abgelehnt.',
    bringUnreachable: 'Bring! ist gerade nicht erreichbar.',
    bringSignIn: 'Bring! möchte eine neue Anmeldung.',
    bringWrong: 'E-Mail oder Passwort stimmen nicht.',
    bringOdd: 'Bring! antwortet anders als erwartet.',
    // Larovi Plus and recipes (1.21)
    noFamily: 'Diese Familie gibt es nicht.',
    plusOnly: 'Das gehört zu Larovi Plus.',
    recipeGone: 'Dieses Rezept gibt es nicht mehr.',
    voteOwn: 'Abstimmen kann jeder nur für sich.',
    noMealPlanned: 'Für diesen Tag ist nichts geplant.',
    recipesFull: (n: string) =>
      `Eure ${n} Rezepte sind voll. Mit Larovi Plus könnt ihr beliebig viele speichern.`,
    pageNotOpening: 'Die Seite ließ sich nicht öffnen. Stimmt der Link?',
    pageDown: 'Die Seite antwortet gerade nicht. Bitte später nochmal.',
    noRecipeOnPage:
      'Auf dieser Seite haben wir kein lesbares Rezept gefunden. Schick einen Screenshot oder schreib es selbst auf.',
    pickPicture: 'Bitte ein Foto oder einen Screenshot wählen.',
    noRecipeInPicture:
      'Darin haben wir kein Rezept erkannt. Ein schärferes Foto hilft – oder schreib es selbst auf.',
    failedLater: 'Das hat nicht geklappt. Bitte später nochmal versuchen.',
  },
  en: {
    signIn: 'Please sign in first.',
    parentsOnly: 'Only parents can do that.',
    parentsTeens: 'Only parents and teens can do that.',
    badInput: 'The input is incomplete or invalid.',
    noHousehold: 'This account doesn’t belong to a household yet.',
    hasHousehold: 'This account already has a household.',
    reload: 'Please reload the page and try again.',
    emailOrGoogle: 'Please sign in with email or Google.',
    badInvite: 'The code is invalid or has expired.',
    parentsStay: 'Parents stay parents.',
    kidsUseWall: 'Kids use the family device.',
    confirmItsYou: 'Please confirm once more that it’s you.',
    passwordOnly: 'This only works for accounts with email and password.',
    sameAddress: 'That’s already your address.',
    noEmail: 'This account has no email.',
    sentAlready: 'Just sent – please check your inbox.',
    sentAlreadyNew: 'Just sent – please check the inbox of the new address.',
    emailTaken: 'There’s already an account with this email.',
    noPage: 'This page doesn’t exist.',
    exportParents: 'Only parents can download the data export.',
    feedbackLimit: 'Thank you – that was a lot at once. Please try again later.',
    notEnoughStars: 'There aren’t enough stars for that yet.',
    requestGone: 'This request no longer exists.',
    taskGone: 'This task no longer exists.',
    noPerson: 'This person doesn’t exist.',
    badPairCode: 'The code is wrong or has expired.',
    deviceGone: 'This device is not paired.',
    pairLimit: 'Too many wrong codes. Please try again in a quarter of an hour.',
    pinWaitOne: 'Too many attempts. Please wait a minute.',
    pinWait: (minutes: string) => `Too many attempts. Please wait ${minutes} minutes.`,
    aiEmpty: 'Please type, say something or choose a photo.',
    aiOff: 'The AI helper is turned off. Parents can turn it on in Settings.',
    aiLimit: (n: string) =>
      `The AI helper has already been used ${n} times today. It works again tomorrow.`,
    aiPauseApp:
      'The AI helper is taking a break for today – it will be back tomorrow. Until then, just add it by hand.',
    aiPauseFamily:
      'Your AI requests for today are used up – they will be back tomorrow. Until then, just add it by hand.',
    aiNothing: 'The AI couldn’t suggest anything for that. Try different words.',
    aiBusy:
      'The AI helper is busy right now. It works again in a few minutes – or just add it by hand now.',
    aiUnavailable: 'The AI helper isn’t available right now. Please try again later.',
    aiGarbled: 'The AI gave an answer that didn’t make sense. Please try again.',
    aiUnreachable: 'The AI can’t be reached right now. Please try again in a moment.',
    aiUnreachableShort: 'The AI can’t be reached right now.',
    aiParents: 'Only parents use the AI helper – on the wall display with the parent PIN.',
    aiSignIn: 'Signing in to the AI didn’t work.',
    wallOnly: 'Only on the wall display.',
    aiQuota: 'The AI is busy right now.',
    aiKey: 'Google doesn’t accept the key.',
    aiRefused: 'The AI didn’t want to answer that.',
    aiGarbledShort: 'The AI gave an answer that didn’t make sense.',
    notCalendarLink: 'That isn’t a calendar link (https:// or webcal://).',
    calendarUnreachable: 'The calendar can’t be reached. Was the whole link copied?',
    noCalendar: 'There’s no calendar at this link. Please use the iCal/ICS link.',
    calendarStatus: (status: string) => `Calendar can’t be reached (${status})`,
    noCalendarLink: 'No calendar link saved',
    badLink: 'That isn’t a valid link.',
    tooBig: 'The calendar is too big.',
    notPublic: 'This link doesn’t point to a public address.',
    httpsOnly: 'Only links with https:// or http:// work.',
    redirects: 'The link redirects too often.',
    wasteDown: 'The waste service isn’t responding right now. Please try again later.',
    wasteUnknown: 'We don’t know this waste company yet.',
    wasteNoDates: 'There are no collection days for this street.',
    wasteNoData: 'Waste service: no data',
    wasteNoEvents: 'Waste service: no collection days',
    wasteStatus: (status: string) => `Waste service not responding (${status})`,
    wasteBadAddress: 'Invalid address',
    wasteNoAddress: 'No address saved for bin collection',
    bringAuth: 'Bring! wants you to sign in again – reconnect in Settings under “Shopping list”.',
    bringDown: 'Bring! can’t be reached right now. Please try again in a moment.',
    bringChanged:
      'Bring! has changed something – the shopping list isn’t working right now. We’re on it.',
    bringNotConnected: 'Bring! isn’t connected yet.',
    bringNoList: 'This list doesn’t exist in Bring! (any more).',
    bringEmptyAccount: 'There’s no list in this Bring! account yet.',
    bringRejected: 'Bring! turned down the sign-in.',
    bringUnreachable: 'Bring! can’t be reached right now.',
    bringSignIn: 'Bring! wants you to sign in again.',
    bringWrong: 'Email or password is wrong.',
    bringOdd: 'Bring! is answering differently than expected.',
    // Larovi Plus and recipes (1.21)
    noFamily: 'This family doesn’t exist.',
    plusOnly: 'This is part of Larovi Plus.',
    recipeGone: 'This recipe no longer exists.',
    voteOwn: 'Everyone can only vote for themselves.',
    noMealPlanned: 'Nothing is planned for this day.',
    recipesFull: (n: string) =>
      `Your ${n} recipes are full. With Larovi Plus you can save as many as you like.`,
    pageNotOpening: 'The page couldn’t be opened. Is the link right?',
    pageDown: 'The page isn’t answering right now. Please try again later.',
    noRecipeOnPage:
      'We couldn’t find a readable recipe on this page. Send a screenshot or write it down yourself.',
    pickPicture: 'Please choose a photo or a screenshot.',
    noRecipeInPicture:
      'We couldn’t make out a recipe in it. A sharper photo helps – or write it down yourself.',
    failedLater: 'That didn’t work. Please try again later.',
  },
} as const;

type Entry = string | ((...a: string[]) => string);
const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** German message → the same message in `locale` (unknown messages stay as they are). */
export function translateServer(message: string, locale: Locale): string {
  if (locale === 'de') return message;
  const de = SERVER_TEXT.de as Record<string, Entry>;
  const to = SERVER_TEXT[locale] as Record<string, Entry>;
  for (const [key, entry] of Object.entries(de)) {
    const target = to[key]!;
    if (typeof entry === 'string') {
      if (entry === message) return target as string;
      continue;
    }
    // a text with blanks: the German pattern finds the values, the translation takes them
    const blanks = Array.from({ length: entry.length }, (_, i) => `⟦${i}⟧`);
    const pattern = escape(entry(...blanks)).replace(/⟦\d⟧/g, '(.+?)');
    const m = new RegExp(`^${pattern}$`).exec(message);
    if (m) return (target as (...a: string[]) => string)(...m.slice(1));
  }
  return message;
}
